import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { PageHeader, vibrantTheme, VIBRANT_THEMES } from "../components/common";
import { useArticleSlugs, matchArticleSlug } from "../components/ArticleSections";
import api from "../lib/api";

/* "Explore • Plan • Share • Travel" — user-supplied flagship article. Text is verbatim. */

const S = [
  {
    e: "\u{1F30D}", t: "Explore",
    paras: [
      "Every journey starts with discovery.",
      "You may already know exactly where you want to go, or you may simply be looking for inspiration.",
      "TravelerPulse.pro is designed to help you explore:",
    ],
    chips: ["Countries", "States and provinces", "Cities and towns", "Airports", "Airlines", "Railway stations", "Train routes", "Bus networks", "Cruise ports", "Ferries", "Rideshare", "Taxis", "Rental cars", "Hotels", "Resorts", "Restaurants", "Caf\u00e9s", "Attractions", "Beaches", "Mountains", "Parks", "Museums", "Historic places", "Local events", "Scenic routes", "Travel stories", "Travel news"],
    after: [
      "A destination should not exist as an isolated page. It should connect naturally to everything around it.",
      "A city should connect to its airport. The airport should connect to airlines, taxis, rideshare, trains and hotels. A railway station should connect to nearby restaurants and accommodations. A cruise port should connect to transportation, hotels and attractions.",
      "TravelerPulse.pro aims to make the world easier to explore by showing those connections.",
    ],
  },
  {
    e: "\u2708\uFE0F", t: "Explore by Transportation",
    paras: [
      "Sometimes the journey itself is part of the attraction.",
      "TravelerPulse.pro can help travelers discover the world through:",
    ],
    cards: [
      ["Air", "Airlines, airports, terminals, lounges, routes and airport services."],
      ["Rail", "High-speed trains, intercity rail, commuter systems, scenic railways and railway stations."],
      ["Bus", "Local buses, long-distance coaches, airport buses and international bus routes."],
      ["Taxi & Ground Transportation", "Taxis, shuttles, private transfers, limousines, rental cars and other local transportation."],
      ["Rideshare", "App-based transportation, ride-hailing, taxi apps and local mobility services."],
      ["Road", "Road trips, highways, scenic drives, parking, rental cars and EV charging."],
      ["Water", "Cruises, ferries, passenger ships, water taxis, ports and river transportation."],
    ],
    after: ["Travelers should be able to explore transportation as part of the destination, not just as a way of getting there."],
  },
  {
    e: "\u{1F5FA}", t: "Explore by Place",
    paras: ["TravelerPulse.pro can organize the world geographically:"],
    flow: ["World", "Continent", "Country", "State/Province", "City/Town", "Neighborhood", "Place"],
    mid: ["From there, travelers can discover:"],
    chips: ["Transportation", "Accommodation", "Dining", "Attractions", "Shopping", "Events", "Local services", "Travel communities"],
    after: ["This makes it possible to start with a country, city, airport, hotel or attraction and continue discovering related places."],
  },
  {
    e: "\u{1F9ED}", t: "Plan",
    paras: [
      "Once you know where you want to go, the next question is: How do I put the journey together?",
      "Planning a trip can involve dozens of decisions. TravelerPulse.pro is designed to bring those decisions closer together.",
      "Plan:",
    ],
    chips: ["Flights", "Trains", "Buses", "Cruises", "Ferries", "Airport transfers", "Taxis", "Rideshare", "Rental cars", "Hotels", "Restaurants", "Attractions", "Events", "Road trips", "Connections", "Travel documents"],
    after: ["Instead of thinking of each component separately, TravelerPulse.pro encourages travelers to look at the complete journey."],
  },
  {
    e: "\u{1F6C2}", t: "Prepare Before You Travel",
    paras: [
      "International travel requires more than reservations.",
      "Travelers may also need to understand:",
    ],
    chips: ["Passport requirements", "Visa requirements", "eVisa systems", "Electronic travel authorizations", "Transit visas", "Immigration", "Customs", "Entry requirements", "Embassy contacts", "Consulate contacts"],
    after: [
      "TravelerPulse can connect travelers with official government information and diplomatic resources relevant to their destination.",
      "Because passport and visa requirements can change, travelers should always verify current requirements directly with the appropriate government authority, embassy or consulate.",
    ],
    cta: ["Open Visas & Embassies", "/visas"],
  },
  {
    e: "\u{1F504}", t: "Plan the Complete Journey",
    paras: ["A trip might look like:"],
    flow: ["Home", "Taxi", "Airport", "Flight", "Train", "Hotel", "Restaurant", "Attraction", "Cruise Port", "Cruise", "Ferry", "Bus", "Hotel", "Airport", "Home"],
    mid: [
      "TravelerPulse.pro aims to connect each part.",
      "A traveler should be able to understand not only how to reach a destination, but also:",
    ],
    chips: ["Where to stay", "How to get around", "Where to eat", "What to see", "What documents may be required", "What other travelers have experienced"],
  },
  {
    e: "\u{1F3E8}", t: "Plan Where to Stay",
    paras: ["Accommodation is a major part of every trip. Explore and plan stays including:"],
    chips: ["Hotels", "Resorts", "Motels", "Hostels", "Vacation rentals", "Homestays", "Bed & breakfasts", "Extended-stay properties", "Timeshares"],
    mid: ["TravelerPulse.pro can connect accommodations with:"],
    chips2: ["Airports", "Railway stations", "Cruise ports", "Restaurants", "Attractions", "Ground transportation"],
    after: ["This can make it easier to choose a place to stay based on the entire trip rather than price alone."],
  },
  {
    e: "\u{1F37D}", t: "Plan Where to Eat",
    paras: ["Travel and food are inseparable. TravelerPulse.pro can help travelers discover:"],
    chips: ["Restaurants", "Caf\u00e9s", "Bakeries", "Street food", "Food markets", "Food halls", "Hotel restaurants", "Airport dining", "Railway-station dining", "Local cuisine"],
    after: ["Travelers can explore what a destination is known for and find dining near their hotel, airport, station or attraction."],
  },
  {
    e: "\u{1F4CD}", t: "Plan What to Do",
    paras: ["A destination becomes memorable because of what you experience there. TravelerPulse.pro can connect travelers with:"],
    chips: ["Attractions", "Museums", "Historic sites", "Beaches", "Parks", "Mountains", "Gardens", "Markets", "Festivals", "Sporting events", "Cultural events", "Shopping", "Scenic viewpoints", "Local experiences"],
    after: ["Planning becomes more useful when transportation, accommodation and attractions are linked together."],
  },
  {
    e: "\u{1F9F3}", t: "My Travel Hub",
    paras: ["TravelerPulse.pro can make planning more personal through My Travel Hub. Travelers can use it to organize:"],
    chips: ["Trips", "Destinations", "Flights", "Trains", "Buses", "Cruises", "Hotels", "Restaurants", "Attractions", "Transportation", "Visa information", "Embassy and consulate links", "Travel notes", "Saved articles", "Favorite places", "Photos", "Travel stories"],
    after: ["The idea is to create one personal space for the entire journey."],
    cta: ["Open My Travel Hub", "/travel-hub"],
  },
  {
    e: "\u{1F4AC}", t: "Share",
    paras: [
      "Travel knowledge becomes more useful when travelers share it.",
      "TravelerPulse.pro is being designed as more than a travel directory. It is a community.",
      "Members can share:",
    ],
    chips: ["Travel stories", "Photos", "Reviews", "Tips", "Recommendations", "Questions", "Answers", "Complaints", "Suggestions", "Transportation experiences", "Hotel experiences", "Restaurant experiences", "Destination experiences"],
    after: ["A traveler who has just completed a journey may have information that can help someone making the same trip tomorrow."],
    cta: ["Join the Community", "/community"],
  },
  {
    e: "\u{1F4F8}", t: "Share Your Travel Photos",
    paras: ["Travel is visual. Photos can tell the story of:"],
    chips: ["Airports", "Trains", "Buses", "Cruise ships", "Hotels", "Restaurants", "Beaches", "Mountains", "Cities", "Villages", "Markets", "Sunsets", "Road trips", "Local life"],
    after: [
      "TravelerPulse.pro can connect photographs to the appropriate place, route, business or transportation profile.",
      "Over time, these images can create a community-built visual record of travel around the world.",
    ],
  },
  {
    e: "\u270D\uFE0F", t: "Share Your Travel Stories",
    paras: ["Every journey has a story. It may be:"],
    chips: ["A memorable family vacation", "A train journey across a country", "A missed connection", "An unexpected friendship", "A wonderful local meal", "A cruise experience", "A difficult airport transfer", "A road trip", "A hidden destination", "A travel lesson"],
    after: ["TravelerPulse.pro gives travelers a place to preserve and share those experiences."],
  },
  {
    e: "\u2B50", t: "Share Reviews",
    paras: ["Traveler reviews can help other people make better decisions. Members can share experiences involving:"],
    chips: ["Airlines", "Airports", "Railway operators", "Bus companies", "Taxi services", "Rideshare companies", "Hotels", "Restaurants", "Cruise lines", "Cruise ships", "Attractions"],
    after: ["Reviews should remain clearly separated from official information provided by businesses or transportation authorities."],
  },
  {
    e: "\u26A0\uFE0F", t: "Share When Things Go Wrong",
    paras: ["Travel does not always go according to plan."],
    chips: ["Flights are delayed.", "Baggage is lost.", "Trains are cancelled.", "Hotels disappoint.", "Taxi pickups fail.", "Cruise itineraries change."],
    after: [
      "TravelerPulse.pro can provide a place where travelers can describe what happened, ask questions and share useful information.",
      "The goal should be constructive discussion, not simply complaints. Businesses and travel organizations may also be able to respond and clarify legitimate issues.",
    ],
    cta: ["Complaints Forum", "/complaints"],
  },
  {
    e: "\u{1F91D}", t: "Help Other Travelers",
    paras: ["Sometimes the most useful travel advice is very simple. A traveler might explain:"],
    chips: ["Which airport terminal to use", "Where the rideshare pickup area is", "Which railway exit is closest to a hotel", "Whether a bus accepts cash", "How to reach the cruise port", "Which restaurant is open late", "Whether a station has an elevator", "Which consulate serves a particular region"],
    after: [
      "Small pieces of practical information can make a big difference.",
      "TravelerPulse.pro aims to make that knowledge easier to share.",
    ],
  },
  {
    e: "\u{1F680}", t: "Travel",
    paras: [
      "After exploring and planning comes the most important part: Go.",
      "TravelerPulse.pro is not meant to replace the experience of travel. It is meant to make the journey easier to understand before you leave, more connected while you are traveling, and easier to remember and share when you return.",
      "Travel may be:",
    ],
    chips: ["Across town", "Across a state", "Across a country", "Across an ocean", "Around the world"],
    after: ["Every journey matters to the person taking it."],
  },
  {
    e: "\u{1F310}", t: "Stay Connected Along the Journey",
    paras: ["TravelerPulse.pro can help travelers move naturally from one stage to the next. For example:"],
    flow: ["Airport", "Find transportation", "Reach hotel", "Discover restaurants", "Explore attractions", "Find railway station", "Continue to next city", "Check passport or visa resources", "Share the journey"],
    after: ["The platform is designed around these connections."],
  },
  {
    e: "\u{1F4F0}", t: "Follow What Is Happening",
    paras: ["Travel changes constantly. TravelerPulse.pro can bring together travel news about:"],
    chips: ["Airlines", "Airports", "Rail", "Buses", "Rideshare", "Ground transportation", "Cruises", "Hotels", "Destinations", "Visas", "Transportation projects", "New routes", "Service changes"],
    after: ["News can connect directly to relevant profiles throughout the platform."],
    cta: ["Travel News", "/articles"],
  },
  {
    e: "\u{1F30D}", t: "Travel Locally and Globally",
    paras: [
      "TravelerPulse.pro is intended for both international travelers and people exploring closer to home.",
      "You do not need to cross an ocean to travel. A journey can be:",
    ],
    chips: ["A local train ride", "A weekend road trip", "A ferry across a bay", "A bus to a neighboring city", "A visit to a local market", "A hike", "A museum visit", "A restaurant discovery"],
    after: ["TravelerPulse.pro celebrates travel in all its forms."],
  },
  {
    e: "\u{1F465}", t: "A Community for Every Kind of Traveler",
    paras: ["TravelerPulse.pro can serve:"],
    chips: ["Leisure travelers", "Families", "Senior travelers", "Business travelers", "Students", "Backpackers", "Frequent flyers", "Rail travelers", "Road travelers", "Cruise travelers", "Photographers", "Travel writers", "Local residents", "Transportation enthusiasts"],
    after: ["Local residents are especially valuable because they often know their communities better than any travel guide."],
  },
];

const PILLARS = [
  ["\u{1F30D}", "Explore", "Discover destinations, transportation, places, people and possibilities."],
  ["\u{1F9ED}", "Plan", "Connect the pieces of your journey and prepare before you leave."],
  ["\u{1F4AC}", "Share", "Tell your story, post your photos, share your experience and help other travelers."],
  ["\u2708\uFE0F", "Travel", "Go somewhere. Experience it. Learn from it. Enjoy the journey."],
];

const MODE_LINKS = {
  "Air": { to: "/guides/air", category: "Air Travel" },
  "Rail": { to: "/guides/rail", category: "Rail Travel" },
  "Bus": { to: "/guides/bus", category: "Bus & Coach Travel" },
  "Taxi & Ground Transportation": { to: "/guides/ground", category: "Ground Transportation" },
  "Rideshare": { to: "/guides/rideshare", category: "Rideshare" },
  "Road": { to: "/guides/road", category: "Road & Driving" },
  "Water": { to: "/guides/water", category: "Cruises & Water Travel" },
};

const Chips = ({ items, theme, testid }) => {
  const slugs = useArticleSlugs();
  return (
    <div className="flex flex-wrap gap-2 my-4" data-testid={testid}>
      {items.map((c) => {
        const slug = matchArticleSlug(c, slugs);
        return slug ? (
          <Link key={c} to={`/articles/${slug}`} data-testid={`story-chip-link-${slug}`}
            className="px-3 py-1.5 text-sm border font-600 underline decoration-dotted underline-offset-2 hover:decoration-solid transition-colors"
            style={{ borderColor: theme.bg, color: theme.bg, backgroundColor: `${theme.bg}1A` }}>
            {c}
          </Link>
        ) : (
          <span key={c} className="px-3 py-1.5 text-sm border font-600"
            style={{ borderColor: theme.bg, color: theme.bg, backgroundColor: `${theme.bg}0D` }}>
            {c}
          </span>
        );
      })}
    </div>
  );
};

const ModeCards = ({ cards }) => {
  const [byCat, setByCat] = useState({});
  useEffect(() => {
    api.get("/articles-imported").then((r) => {
      const m = {};
      (r.data.items || []).forEach((a) => {
        if (a.entity_type === "topic" && a.category) (m[a.category] = m[a.category] || []).push(a);
      });
      setByCat(m);
    }).catch(() => {});
  }, []);

  return (
    <div className="grid sm:grid-cols-2 gap-3 my-4">
      {cards.map(([name, desc], i) => {
        const ct = VIBRANT_THEMES[i % VIBRANT_THEMES.length];
        const link = MODE_LINKS[name];
        const arts = (link && byCat[link.category]) || [];
        return (
          <div key={name} className="border border-zinc-300 bg-white p-4 border-l-4" style={{ borderLeftColor: ct.bg }}>
            {link ? (
              <Link to={link.to} data-testid={`mode-card-link-${link.to.split("/").pop()}`}
                className="font-heading font-700 underline decoration-dotted underline-offset-4 hover:decoration-solid" style={{ color: ct.bg }}>
                {name} →
              </Link>
            ) : <div className="font-heading font-700" style={{ color: ct.bg }}>{name}</div>}
            <p className="text-sm text-muted-foreground mt-1">{desc}</p>
            {arts.length > 0 && (
              <ul className="mt-2.5 space-y-1 border-t border-zinc-200 pt-2">
                {arts.slice(0, 3).map((a) => (
                  <li key={a.slug}>
                    <Link to={`/articles/${a.slug}`} data-testid={`mode-article-${a.slug}`}
                      className="text-xs text-zinc-700 hover:text-primary hover:underline transition-colors">{a.title}</Link>
                  </li>
                ))}
                <li><Link to="/articles#group-topics" className="text-xs font-700 hover:underline" style={{ color: ct.bg }}>All {arts.length} {name} guides →</Link></li>
              </ul>
            )}
          </div>
        );
      })}
    </div>
  );
};

const Flow = ({ items, theme }) => (
  <div className="flex flex-wrap items-center gap-y-2 my-4">
    {items.map((step, i) => (
      <span key={`${step}-${i}`} className="flex items-center">
        <span className="px-2.5 py-1 text-sm font-700 text-white" style={{ backgroundColor: theme.bg }}>{step}</span>
        {i < items.length - 1 && <span className="mx-1.5 font-mono text-sm" style={{ color: theme.bg }}>{"\u2192"}</span>}
      </span>
    ))}
  </div>
);

export default function OurStory() {
  return (
    <div className="min-h-screen bg-background" data-testid="our-story-page">
      <PageHeader
        eyebrow="TravelerPulse.pro"
        title={"Explore \u2022 Plan \u2022 Share \u2022 Travel"}
        subtitle="One Journey. One Connected Travel Community."
        entityCode="explore-plan-share-travel"
        unsplashQuery="world map travel photography passport"
      />

      <div className="max-w-4xl mx-auto px-5 lg:px-8 py-12 space-y-14">
        <section className="space-y-4 text-zinc-700 leading-relaxed text-base" data-testid="story-intro">
          <p className="font-heading font-700 text-xl text-foreground">
            Travel is more than moving from one place to another.
          </p>
          <p>
            It begins with an idea, grows through planning, becomes an experience, and lives on through the stories,
            photographs, reviews and memories we share. That is the idea behind TravelerPulse.pro.
          </p>
          <p>
            Our goal is to create a connected global travel community where people can Explore, Plan, Share and Travel
            using information about transportation, destinations, accommodations, dining, travel services and the
            experiences of other travelers.
          </p>
          <p>
            Whether you are planning a weekend road trip, an international flight, a train journey, a cruise, a family
            vacation or a trip around the world, TravelerPulse.pro is designed to help connect the pieces.
          </p>
        </section>

        {S.map((s, idx) => {
          const theme = VIBRANT_THEMES[idx % VIBRANT_THEMES.length];
          const slug = s.t.toLowerCase().replace(/[^a-z]+/g, "-");
          return (
            <section key={s.t} data-testid={`story-section-${slug}`}>
              <div className="flex items-center gap-3 mb-4">
                <span className="w-10 h-10 flex items-center justify-center text-lg text-white shrink-0" style={{ backgroundColor: theme.bg }} aria-hidden="true">{s.e}</span>
                <h2 className="font-heading font-900 text-2xl sm:text-3xl tracking-tight">{s.t}</h2>
              </div>
              <div className="space-y-3 text-zinc-700 leading-relaxed text-base">
                {s.paras?.map((p, i) => <p key={i}>{p}</p>)}
                {s.flow && <Flow items={s.flow} theme={theme} />}
                {s.chips && !s.flow && <Chips items={s.chips} theme={theme} testid={`story-chips-${slug}`} />}
                {s.cards && <ModeCards cards={s.cards} />}
                {s.mid?.map((p, i) => <p key={`m${i}`}>{p}</p>)}
                {s.chips && s.flow && <Chips items={s.chips} theme={theme} testid={`story-chips-${slug}`} />}
                {s.chips2 && <Chips items={s.chips2} theme={theme} testid={`story-chips2-${slug}`} />}
                {s.after?.map((p, i) => <p key={`a${i}`}>{p}</p>)}
                {s.cta && (
                  <Link to={s.cta[1]} data-testid={`story-cta-${slug}`}
                    className="inline-flex items-center gap-1 text-sm font-700 hover:underline" style={{ color: theme.bg }}>
                    {s.cta[0]} {"\u2192"}
                  </Link>
                )}
              </div>
            </section>
          );
        })}

        <section data-testid="story-philosophy">
          <h2 className="font-heading font-900 text-2xl sm:text-3xl tracking-tight mb-3">The TravelerPulse Philosophy</h2>
          <p className="text-zinc-700 leading-relaxed mb-5">The idea behind TravelerPulse.pro can be summarized in four words:</p>
          <div className="grid sm:grid-cols-2 gap-4">
            {PILLARS.map(([e, t, d], i) => {
              const theme = VIBRANT_THEMES[i];
              return (
                <div key={t} className="text-white p-6" style={{ backgroundColor: theme.bg }} data-testid={`philosophy-${t.toLowerCase()}`}>
                  <div className="text-2xl mb-2" aria-hidden="true">{e}</div>
                  <div className="font-heading font-900 text-xl">{t}</div>
                  <p className="text-sm text-white/90 mt-2 leading-relaxed">{d}</p>
                </div>
              );
            })}
          </div>
          <p className="text-zinc-700 leading-relaxed mt-5">Then come back and help the next traveler.</p>
        </section>

        <section className="text-white p-8 sm:p-12 text-center" style={{ backgroundColor: vibrantTheme("story-closing").bg }} data-testid="story-closing">
          <div className="font-heading font-900 text-2xl sm:text-3xl">TravelerPulse.pro</div>
          <div className="font-mono text-sm tracking-[0.18em] text-white/90 mt-3">{"Explore \u2022 Plan \u2022 Share \u2022 Travel"}</div>
          <p className="text-white/90 leading-relaxed mt-6 max-w-2xl mx-auto">
            Travel is not simply about reaching a destination. It is about discovering the world, connecting the journey
            and sharing what we learn along the way.
          </p>
          <p className="text-white font-700 mt-3">That is the community TravelerPulse.pro aims to build.</p>
        </section>
      </div>
    </div>
  );
}
